// src/lib/filters.ts — FILTRE SALVATE ca straturi derivate (decizia 2.2(b), BD-012). PUR, partajat
// server↔client (ca lib/collect, lib/rlu): gramatica filtrului, validarea FAIL-CLOSED la scriere
// (PATCH layer `saved_filters`), parsarea LENIENTĂ la citire (config-ul e jsonb liber) și predicatul
// în memorie `filter_matches` — ACEEAȘI semantică pe care serverul o traduce în SQL
// (`saved_filter_sql`, features.ts). Clientul îl aplică pe straturile GeoJSON (fără round-trip), iar
// tests/filters.test.ts verifică PARITATEA SQL ↔ JS pe aceleași features.
//
// Semantica (o singură regulă, ușor de explicat): comparația se face pe TEXTUL valorii (cum îl dă
// `value #>> '{}'` în Postgres / `String(v)` în JS), CU EXCEPȚIA cazului în care valoarea filtrului
// e un NUMĂR — atunci proprietatea se citește numeric (număr JSON sau text numeric; altfel nu se
// potrivește). `contains` e case-insensitive. `ne` include și elementele FĂRĂ atribut („≠ aprobat"
// = tot ce nu e aprobat, ca într-o foaie de calcul); `eq null`/`ne null` = lipsă/prezență.
// Ordonările pe text folosesc comparația bytewise (COLLATE "C" în SQL) — pentru date, format ISO.

import { SYSTEM_FEATURE_KEYS } from "./collect"

export const FILTER_OPS = ["eq", "ne", "lt", "lte", "gt", "gte", "contains"] as const
export type FilterOp = (typeof FILTER_OPS)[number]
export type FilterValue = string | number | boolean | null

export interface FilterClause {
	attr: string
	op: FilterOp
	value: FilterValue
}

/** Un filtru salvat pe strat: `filter` = clauze legate prin ȘI (AND). */
export interface SavedFilter {
	slug: string
	name: string
	filter: FilterClause[]
}

export const MAX_SAVED_FILTERS = 20
export const MAX_FILTER_CLAUSES = 10
const MAX_TEXT_VALUE = 200
const SLUG_RE = /^[a-z0-9][a-z0-9_]{0,39}$/

/** Aliasuri acceptate la scriere (simboluri, cuvinte RO) → operatorul canonic. */
const OP_ALIASES: Record<string, FilterOp> = {
	eq: "eq",
	"=": "eq",
	"==": "eq",
	ne: "ne",
	"!=": "ne",
	"<>": "ne",
	"≠": "ne",
	lt: "lt",
	"<": "lt",
	lte: "lte",
	"<=": "lte",
	"≤": "lte",
	gt: "gt",
	">": "gt",
	gte: "gte",
	">=": "gte",
	"≥": "gte",
	contains: "contains",
	conține: "contains",
	contine: "contains",
	"~": "contains",
	like: "contains",
	ilike: "contains",
}

/** Simbolul de afișare al operatorului (legendă, editor). */
export const FILTER_OP_SYMBOLS: Record<FilterOp, string> = {
	eq: "=",
	ne: "≠",
	lt: "<",
	lte: "≤",
	gt: ">",
	gte: "≥",
	contains: "conține",
}

export function normalize_op(raw: unknown): FilterOp | null {
	if (typeof raw !== "string") {
		return null
	}
	return OP_ALIASES[raw.trim().toLowerCase()] ?? null
}

// ─────────────────────────────────────────────────────────────
// Validare FAIL-CLOSED (la scriere) + parsare lenientă (la citire)
// ─────────────────────────────────────────────────────────────

import { validation_error as err, type Validation } from "./validation"

function is_obj(x: unknown): x is Record<string, unknown> {
	return typeof x === "object" && x !== null && !Array.isArray(x)
}

/** O clauză: atribut (nu de sistem), operator cunoscut, valoare scalară potrivită operatorului. */
export function validate_clause(c: unknown): Validation<FilterClause> {
	if (!is_obj(c)) {
		return err("clauza trebuie să fie un obiect {attr, op, value}")
	}
	const attr = typeof c.attr === "string" ? c.attr.trim() : ""
	if (!attr || attr.length > 80) {
		return err("atributul lipsește sau e prea lung")
	}
	if (SYSTEM_FEATURE_KEYS.includes(attr)) {
		return err(`atributul '${attr}' e de sistem și nu se poate filtra`)
	}
	const op = normalize_op(c.op)
	if (!op) {
		return err(`operator necunoscut '${String(c.op)}' (permise: =, ≠, <, ≤, >, ≥, conține)`)
	}
	const value = c.value
	const kind =
		value === null
			? "null"
			: typeof value === "string"
				? "string"
				: typeof value === "number" && Number.isFinite(value)
					? "number"
					: typeof value === "boolean"
						? "boolean"
						: "bad"
	if (kind === "bad") {
		return err(`valoare nepermisă pentru '${attr}' (doar text, număr, adevărat/fals sau null)`)
	}
	if (kind === "string" && (value as string).length > MAX_TEXT_VALUE) {
		return err(`valoarea pentru '${attr}' depășește ${MAX_TEXT_VALUE} de caractere`)
	}
	if (op === "contains" && (kind !== "string" || (value as string).trim() === "")) {
		return err(`'conține' cere un text nevid (atributul '${attr}')`)
	}
	if (
		(op === "lt" || op === "lte" || op === "gt" || op === "gte") &&
		(kind === "boolean" || kind === "null" || (kind === "string" && value === ""))
	) {
		return err(
			`operatorul ${FILTER_OP_SYMBOLS[op]} cere un număr sau un text (atributul '${attr}')`,
		)
	}
	return { ok: true, value: { attr, op, value: value as FilterValue } }
}

/**
 * Lista de filtre salvate a unui strat — validare completă, fail-closed: prima problemă oprește
 * scrierea cu un mesaj RO care spune UNDE (filtrul #n / clauza #m). `null`/absent = listă goală.
 * Acceptă `filter` ca obiect (o clauză) sau listă (ȘI între clauze); normalizează la listă.
 */
export function validate_saved_filters(v: unknown): Validation<SavedFilter[]> {
	if (v === null || v === undefined) {
		return { ok: true, value: [] }
	}
	if (!Array.isArray(v)) {
		return err("saved_filters trebuie să fie o listă de filtre")
	}
	if (v.length > MAX_SAVED_FILTERS) {
		return err(`cel mult ${MAX_SAVED_FILTERS} filtre salvate per strat`)
	}
	const out: SavedFilter[] = []
	const seen = new Set<string>()
	for (const [i, item] of v.entries()) {
		const at = `filtrul #${i + 1}`
		if (!is_obj(item)) {
			return err(`${at}: trebuie să fie un obiect {slug, name, filter}`)
		}
		const slug = typeof item.slug === "string" ? item.slug.trim() : ""
		if (!SLUG_RE.test(slug)) {
			return err(`${at}: slug invalid '${slug}' (litere mici, cifre și _, max 40)`)
		}
		if (seen.has(slug)) {
			return err(`${at}: slug-ul '${slug}' se repetă`)
		}
		seen.add(slug)
		const name = typeof item.name === "string" ? item.name.trim() : ""
		if (!name || name.length > 80) {
			return err(`${at} ('${slug}'): numele lipsește sau depășește 80 de caractere`)
		}
		const raw = Array.isArray(item.filter) ? item.filter : item.filter != null ? [item.filter] : []
		if (raw.length === 0) {
			return err(`${at} ('${slug}'): fără nicio clauză`)
		}
		if (raw.length > MAX_FILTER_CLAUSES) {
			return err(`${at} ('${slug}'): cel mult ${MAX_FILTER_CLAUSES} clauze`)
		}
		const clauses: FilterClause[] = []
		for (const [j, c] of raw.entries()) {
			const vc = validate_clause(c)
			if (!vc.ok) {
				return err(`${at} ('${slug}'), clauza #${j + 1}: ${vc.error}`)
			}
			clauses.push(vc.value)
		}
		out.push({ slug, name, filter: clauses })
	}
	return { ok: true, value: out }
}

/** Citire LENIENTĂ din config (jsonb liber, posibil string): intrările invalide se ignoră tăcut. */
export function parse_saved_filters(v: unknown): SavedFilter[] {
	let raw = v
	if (typeof raw === "string") {
		try {
			raw = JSON.parse(raw)
		} catch {
			return []
		}
	}
	if (!Array.isArray(raw)) {
		return []
	}
	const out: SavedFilter[] = []
	const seen = new Set<string>()
	for (const item of raw) {
		const one = validate_saved_filters([item])
		if (one.ok && one.value[0] && !seen.has(one.value[0].slug)) {
			seen.add(one.value[0].slug)
			out.push(one.value[0])
		}
	}
	return out
}

/** Filtrul cu slug-ul dat din config-ul stratului (`config.saved_filters`), sau undefined. */
export function find_saved_filter(
	config_saved_filters: unknown,
	slug: string,
): SavedFilter | undefined {
	return parse_saved_filters(config_saved_filters).find((f) => f.slug === slug)
}

/**
 * F10: filtrul e aplicabil PUBLIC doar dacă toate atributele lui sunt expuse (allow-list) — altfel
 * ar fi un oracol pe un atribut ascuns (apartenența la filtru confirmă valoarea). `null` = fără
 * restricție (dar cheile de sistem rămân excluse — validarea nu le lasă oricum să intre).
 */
export function filter_allowed(clauses: FilterClause[], allow: string[] | null): boolean {
	return clauses.every(
		(c) => !SYSTEM_FEATURE_KEYS.includes(c.attr) && (allow === null || allow.includes(c.attr)),
	)
}

// ─────────────────────────────────────────────────────────────
// Predicatul în memorie — oglinda lui saved_filter_sql (features.ts)
// ─────────────────────────────────────────────────────────────

const NUMERIC_TEXT_RE = /^\s*-?[0-9]+(\.[0-9]+)?\s*$/

/** Textul unei proprietăți, ca `value #>> '{}'`: null pentru lipsă/JSON null. */
function prop_text(v: unknown): string | null {
	if (v === null || v === undefined) {
		return null
	}
	if (typeof v === "object") {
		return JSON.stringify(v)
	}
	return String(v)
}

/** Valoarea numerică a unei proprietăți: număr JSON sau text numeric; altfel NaN (nu se potrivește). */
function prop_number(v: unknown): number {
	if (typeof v === "number") {
		return v
	}
	if (typeof v === "string" && NUMERIC_TEXT_RE.test(v)) {
		return Number(v)
	}
	return NaN
}

function clause_matches(props: Record<string, unknown>, c: FilterClause): boolean {
	const raw = props[c.attr]
	const present = raw !== null && raw !== undefined
	const eq = (): boolean => {
		if (c.value === null) {
			return !present
		}
		if (typeof c.value === "number") {
			return prop_number(raw) === c.value
		}
		return prop_text(raw) === String(c.value)
	}
	switch (c.op) {
		case "eq":
			return eq()
		case "ne":
			return c.value === null ? present : !eq()
		case "contains": {
			const t = prop_text(raw)
			return t !== null && t.toLowerCase().includes(String(c.value).toLowerCase())
		}
		default: {
			// lt / lte / gt / gte
			let a: number | string
			let b: number | string
			if (typeof c.value === "number") {
				a = prop_number(raw)
				b = c.value
				if (Number.isNaN(a)) {
					return false
				}
			} else {
				const t = prop_text(raw)
				if (t === null) {
					return false
				}
				a = t
				b = String(c.value)
			}
			return c.op === "lt" ? a < b : c.op === "lte" ? a <= b : c.op === "gt" ? a > b : a >= b
		}
	}
}

/** Toate clauzele (ȘI) se potrivesc pe proprietățile date. Listă goală = totul trece. */
export function filter_matches(props: Record<string, unknown>, clauses: FilterClause[]): boolean {
	return clauses.every((c) => clause_matches(props, c))
}

// ─────────────────────────────────────────────────────────────
// Etichete (legendă / editor)
// ─────────────────────────────────────────────────────────────

function value_label(v: FilterValue): string {
	if (v === null) {
		return "gol"
	}
	if (typeof v === "boolean") {
		return v ? "da" : "nu"
	}
	if (typeof v === "number") {
		return String(v)
	}
	return `„${v}”`
}

/** `suprafata ≥ 500`, `tip conține „loc”`, `stare = gol`. */
export function filter_label(c: FilterClause): string {
	return `${c.attr} ${FILTER_OP_SYMBOLS[c.op]} ${value_label(c.value)}`
}

/** Clauzele unui filtru salvat, legate prin „și". */
export function saved_filter_label(f: SavedFilter): string {
	return f.filter.map(filter_label).join(" și ")
}
